import { db } from '../firebaseConfig';
import { collection, query, where, getDocs, Timestamp, doc, getDoc } from 'firebase/firestore';
import moment from 'moment';

/**
 * Fetches busy slots for a provider on a specific month (or range).
 * Returns an array of objects: { start: Date, end: Date, type: 'service' | 'personal', title: string }
 */
export const getProviderBusySlots = async (proveedorId, startDate, endDate, excludeQuoteId = null) => {
    if (!proveedorId) return [];

    const busySlots = [];
    const startTs = Timestamp.fromDate(startDate);
    const endTs = Timestamp.fromDate(endDate);

    try {
        const quotesQ = query(
            collection(db, "cotizaciones"),
            where("proveedorId", "==", proveedorId),
            where("estatus", "in", [
                "servicio_programado",
                "proveedor_en_camino",
                "proveedor_en_sitio",
                "servicio_en_curso",
                "esperando_aprobacion_final",
                "pendiente_residente"
            ])
        );

        const quotesSnap = await getDocs(quotesQ);
        quotesSnap.forEach(doc => {
            if (excludeQuoteId && doc.id === excludeQuoteId) return;

            const data = doc.data();
            if (data.fechaHoraServicioSugerido) {
                const date = data.fechaHoraServicioSugerido.toDate();
                let durationHours = 2;
                if (data.tiempoServicio) {
                    const str = data.tiempoServicio.toLowerCase();
                    if (str.includes('día') || str.includes('dia')) {
                        const match = str.match(/(\d+)\s*d/);
                        if (match) durationHours = parseInt(match[1]) * 24;
                    } else if (str.includes('minuto')) {
                        const match = str.match(/(\d+)\s*m/);
                        if (match) durationHours = parseInt(match[1]) / 60;
                    } else {
                        const match = str.match(/(\d+)\s*hora/);
                        if (match) durationHours = parseInt(match[1]);
                    }
                }

                const end = new Date(date);
                end.setHours(end.getHours() + durationHours);

                if (date < endDate && end > startDate) {
                    busySlots.push({
                        id: doc.id,
                        start: date,
                        end: end,
                        type: 'service',
                        title: `${data.tipoServicio} - ${data.nombreResidente}`,
                        status: data.estatus
                    });
                }
            }
        });

        const searchStart = new Date(startDate);
        searchStart.setDate(searchStart.getDate() - 7);
        const searchStartTs = Timestamp.fromDate(searchStart);

        const eventsQ = query(
            collection(db, "eventos_proveedor"),
            where("proveedorId", "==", proveedorId),
            where("fechaInicio", ">=", searchStartTs),
            where("fechaInicio", "<=", endTs)
        );

        const eventsSnap = await getDocs(eventsQ);
        eventsSnap.forEach(doc => {
            const data = doc.data();
            const eStart = data.fechaInicio.toDate();
            const eEnd = data.fechaFin.toDate();

            if (eStart < endDate && eEnd > startDate) {
                busySlots.push({
                    id: doc.id,
                    start: eStart,
                    end: eEnd,
                    type: data.tipo || 'personal',
                    title: data.titulo || 'Evento Personal',
                    isPersonal: true
                });
            }
        });

    } catch (error) {
        console.error("Error fetching busy slots:", error);
    }

    return busySlots;
};

/**
 * Checks if a specific slot (Start Date + Duration) overlaps with any busy slot.
 */
export const isSlotAvailable = async (proveedorId, date, durationHours = 2) => {
    const start = new Date(date);
    const end = new Date(date);
    end.setHours(end.getHours() + durationHours);

    const dayStart = new Date(start);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(start);
    dayEnd.setHours(23, 59, 59, 999);

    const busy = await getProviderBusySlots(proveedorId, dayStart, dayEnd);

    return !busy.some(slot => {
        return (start < slot.end && end > slot.start);
    });
};

/**
 * Fetches the provider's schedule configuration from their profile.
 */
export const getProviderSchedule = async (proveedorId) => {
    try {
        const docRef = doc(db, "market_proveedores", proveedorId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists() && docSnap.data().horario) {
            return docSnap.data().horario;
        }
    } catch (e) {
        console.error("Error fetching schedule:", e);
    }
    return null;
};

/**
 * Helper to normalize string keys (remove accents, lowercase)
 */
const normalizeKey = (str) => {
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
};

/**
 * Checks if a specific date is a working day for the provider.
 * @param {string} dateStr - YYYY-MM-DD
 * @param {object} schedule - The provider's horario object
 */
export const isDayWorking = (dateStr, schedule) => {
    if (!schedule) {
        console.warn("[Availability] No schedule provided to isDayWorking. Defaulting to Open.");
        return true;
    }

    // Detect new simplified schedule format
    if (schedule.hasOwnProperty('habilitado') || schedule.hasOwnProperty('horaInicio')) {
        if (!schedule.habilitado) return true; // If not limited, they work every day
        const dayNum = moment(dateStr).day() === 0 ? 7 : moment(dateStr).day();
        return (schedule.dias || []).includes(dayNum);
    }

    const dayIndex = moment(dateStr).day();
    const daysMap = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
    const dayNameRaw = daysMap[dayIndex];
    const dayNameNorm = normalizeKey(dayNameRaw);

    const scheduleKey = Object.keys(schedule).find(k => normalizeKey(k) === dayNameNorm);

    if (!scheduleKey) {
        console.log(`[Availability] Day key ${dayNameRaw} not found in schedule.`);
        return false;
    }

    const isActive = schedule[scheduleKey]?.activo || false;
    console.log(`[Availability] checking ${dateStr} (${dayNameRaw}) -> Key: ${scheduleKey}, Active: ${isActive}`);
    return isActive;
};

/**
 * Returns the working hours/slots for a specific date.
 * Returns an array of time strings: ['09:00', '09:30', ...]
 */
export const getWorkingSlots = (dateStr, schedule) => {
    if (!schedule) return [];

    const slots = [];
    const generate = (start, end) => {
        if (!start || !end) return;
        let current = moment(`${dateStr}T${start}`);
        let endTime = moment(`${dateStr}T${end}`);

        if (endTime.isBefore(current)) {
            if (end === '00:00') endTime.add(1, 'day');
        }

        while (current < endTime) {
            slots.push(current.format('HH:mm'));
            current.add(30, 'minutes');
        }
    };

    // Detect new simplified schedule format
    if (schedule.hasOwnProperty('habilitado') || schedule.hasOwnProperty('horaInicio')) {
        if (!isDayWorking(dateStr, schedule)) return [];
        generate(schedule.horaInicio || '09:00', schedule.horaFin || '18:00');
        return slots;
    }

    const dayIndex = moment(dateStr).day();
    const daysMap = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
    const dayNameRaw = daysMap[dayIndex];
    const dayNameNorm = normalizeKey(dayNameRaw);

    const scheduleKey = Object.keys(schedule).find(k => normalizeKey(k) === dayNameNorm);

    if (!scheduleKey) return [];

    const config = schedule[scheduleKey];
    if (!config || !config.activo) return [];

    if (config.tipo === 'corrido') {
        generate(config.inicio1 || '09:00', config.fin1 || '18:00');
    } else {
        generate(config.inicio1 || '09:00', config.fin1 || '14:00');
        generate(config.inicio2 || '16:00', config.fin2 || '19:00');
    }

    return slots;
};
